import { useId, useState } from 'react';

const INPUT_CLASSES =
'min-h-11 w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-base text-ink placeholder:text-inksoft/60 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 aria-[invalid=true]:border-brand sm:text-sm';

function FieldWrapper({ label, error, hint, children }) {
  const id = useId();
  const messageId = `${id}-msg`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold text-ink">{label}</label>
      {children(id, error || hint ? messageId : undefined)}
      {error ?
      <p id={messageId} role="alert" className="text-xs font-medium text-brand">{error}</p> :
      hint ?
      <p id={messageId} className="text-xs text-inksoft">{hint}</p> :
      null}
    </div>);

}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>);

}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3l18 18" />
      <path d="M10.6 5.1A10.8 10.8 0 0 1 12 5c6.4 0 10 7 10 7a18 18 0 0 1-3.2 4.2" />
      <path d="M6.6 6.6C3.9 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>);

}

export function TextField({ label, error, hint, type, className, ...rest }) {
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);
  const inputType = isPassword && showPassword ? 'text' : type;
  const inputClasses = `${className || INPUT_CLASSES}${isPassword ? ' pr-12' : ''}`;

  return (
    <FieldWrapper label={label} error={error} hint={hint}>
      {(id, describedBy) =>
      <div className="relative">
          <input
          id={id}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          {...rest}
          type={inputType}
          className={inputClasses}
          {...isPassword ? { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } : {}} />

          {isPassword &&
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          aria-pressed={showPassword}
          aria-controls={id}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-inksoft transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30">
          
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
        }
        </div>
      }
    </FieldWrapper>);

}

export function TextAreaField({ label, error, hint, ...rest }) {
  return (
    <FieldWrapper label={label} error={error} hint={hint}>
      {(id, describedBy) =>
      <textarea
        id={id}
        rows={4}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={INPUT_CLASSES}
        {...rest} />

      }
    </FieldWrapper>);

}

export function SelectField({ label, error, hint, options, ...rest }) {
  return (
    <FieldWrapper label={label} error={error} hint={hint}>
      {(id, describedBy) =>
      <select
        id={id}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={INPUT_CLASSES}
        {...rest}>
        
          <option value="">Select an option</option>
          {options.map((o) =>
        <option key={o.value} value={o.value}>{o.label}</option>
        )}
        </select>
      }
    </FieldWrapper>);

}

export function CheckboxField({ label, error, ...rest }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="flex items-start gap-2.5 text-sm text-ink">
        <input
          id={id}
          type="checkbox"
          aria-invalid={!!error}
          className="mt-0.5 h-4 w-4 rounded border-line accent-[#e8374a]"
          {...rest} />
        
        <span>{label}</span>
      </label>
      {error && <p role="alert" className="text-xs font-medium text-brand">{error}</p>}
    </div>);

}